import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Navigation, Target, Route, Filter, Locate, Loader2, RefreshCw, X, Phone, MapPin, CheckCircle2, Store, Swords, ExternalLink } from 'lucide-react';
import ProspectDrawer from '@/components/ProspectDrawer';

// === Status color mapping ===
const STATUT_COLORS = {
  nouveau: '#9CA3AF',
  a_appeler: '#3B82F6',
  rappel_demande: '#F59E0B',
  injoignable: '#6B7280',
  interesse: '#10B981',
  en_negociation: '#8B5CF6',
  rdv_obtenu: '#F97316',
  converti: '#059669',
  refus: '#EF4444',
  mail_prospection_envoye: '#0055FF',
  mail_doc_envoye: '#8B5CF6',
};
const STATUT_LABELS = {
  nouveau: 'Nouveau', a_appeler: 'À appeler', rappel_demande: 'Rappel', injoignable: 'Injoignable',
  interesse: 'Intéressé', en_negociation: 'En négociation', rdv_obtenu: 'RDV obtenu',
  converti: 'Converti', refus: 'Refus',
  mail_prospection_envoye: 'Mail Prospection', mail_doc_envoye: 'Mail Doc',
};
const SPECIAL_FILTERS = [
  { value: '', label: 'Tous les prospects' },
  { value: 'jamais_contacte', label: '✨ Jamais contactés' },
  { value: 'chaud', label: '🔥 Prospects chauds' },
  { value: 'rdv_proche', label: '📅 RDV obtenus' },
  { value: 'a_relancer', label: '⏰ À relancer' },
  { value: 'concurrent', label: '⚔️ Concurrent en place' },
];

// === Recenter helper ===
function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 }); }, [center, zoom, map]);
  return null;
}

// === Custom markers ===
const makeIcon = (color, selected = false) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="background:${color};width:${selected?22:16}px;height:${selected?22:16}px;border-radius:50%;border:${selected?'3px solid #000':'2px solid #fff'};box-shadow:0 2px 4px rgba(0,0,0,0.3);${selected?'transform:scale(1.2);':''}"></div>`,
  iconSize: [selected ? 22 : 16, selected ? 22 : 16],
  iconAnchor: [selected ? 11 : 8, selected ? 11 : 8],
});

// Diamond/square markers for surround targets & forbidden positions
const makeSquareIcon = (color, emoji = '') => L.divIcon({
  className: 'custom-marker-square',
  html: `<div style="background:${color};width:18px;height:18px;border-radius:3px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.35);transform:rotate(45deg);display:flex;align-items:center;justify-content:center;font-size:10px;">${emoji ? `<span style="transform:rotate(-45deg);">${emoji}</span>` : ''}</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Star-ish markers for curated targets (Intermarché/Système U/Leclerc)
const TARGET_GROUP_COLORS = {
  'Intermarché': '#10B981', // vert
  'Système U':   '#8B5CF6', // violet
  'Leclerc':     '#0055FF', // bleu
};
const makeTargetIcon = (group) => {
  const color = TARGET_GROUP_COLORS[group] || '#F97316';
  return L.divIcon({
    className: 'custom-marker-target',
    html: `<div style="position:relative;width:22px;height:22px;"><div style="position:absolute;inset:0;background:${color};border:2.5px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 6px rgba(0,0,0,0.4);"></div><div style="position:absolute;top:3px;left:4px;width:14px;height:14px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;"><div style="width:8px;height:8px;border-radius:50%;background:${color};"></div></div></div>`,
    iconSize: [22, 28],
    iconAnchor: [11, 28],
  });
};
const formatCA = (v) => {
  if (!v) return '';
  const m = v / 1_000_000;
  return `${m >= 100 ? m.toFixed(0) : m.toFixed(1)} M€`;
};

export default function MapPage() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, localized: 0, pending: 0, failed: 0 });
  const [filters, setFilters] = useState({ enseigne: '', statut: '', departement: '', special: '' });
  const [facets, setFacets] = useState({ enseignes: [], departements: [] });
  const [selected, setSelected] = useState(new Set());
  const [center, setCenter] = useState([46.603354, 1.888334]); // France center
  const [zoom, setZoom] = useState(6);
  const [citySearch, setCitySearch] = useState('');
  const [radiusKm, setRadiusKm] = useState(null);
  const [radiusCenter, setRadiusCenter] = useState(null);
  const [tourneeResult, setTourneeResult] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  // Campaign map (for concurrent name in popup)
  const [campaignsMap, setCampaignsMap] = useState({});
  // Surround (Autour) features
  const [surroundCenter, setSurroundCenter] = useState(null); // [lat, lon]
  const [surroundRadius, setSurroundRadius] = useState(10); // km
  const [surroundSupermarkets, setSurroundSupermarkets] = useState([]);
  const [surroundForbidden, setSurroundForbidden] = useState([]);
  const [surroundLoading, setSurroundLoading] = useState(false);
  const [showSurround, setShowSurround] = useState(false);
  // Prospect fiche drawer
  const [drawerProspect, setDrawerProspect] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  // Curated targets (Intermarché/Système U/Leclerc >30M€)
  const [targets, setTargets] = useState([]);
  const [targetsStats, setTargetsStats] = useState({ total: 0, localized: 0 });
  const [targetFilters, setTargetFilters] = useState({
    showIntermarche: true, showSystemeU: true, showLeclerc: true,
    excludeHanagroup: true, excludeWasabi: true,
  });
  const [showTargets, setShowTargets] = useState(true);
  const [showForbidden, setShowForbidden] = useState(true);
  const [forbiddenPositions, setForbiddenPositions] = useState([]);
  const [forbiddenStats, setForbiddenStats] = useState({ total: 0, localized: 0 });
  // Add-to-campaign modal for targets
  const [targetAddModal, setTargetAddModal] = useState(null); // {target, mode: 'existing'|'new'|'free'}
  const [campaigns, setCampaigns] = useState([]);

  const loadStats = async () => {
    try { const { data } = await api.get('/maps/stats'); setStats(data); } catch {}
  };
  const loadFacets = async () => {
    try { const { data } = await api.get('/prospects/filters/facets'); setFacets(data); } catch {}
  };
  const loadProspects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.enseigne) params.set('enseigne', filters.enseigne);
      if (filters.statut) params.set('statut_commercial', filters.statut);
      if (filters.departement) params.set('departement', filters.departement);
      if (filters.special) params.set('special', filters.special);
      const { data } = await api.get(`/maps/prospects?${params.toString()}`);
      setProspects(data.prospects || []);
    } catch (e) {
      toast.error("Erreur de chargement de la carte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); loadFacets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadProspects(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/campaigns').then(r => {
      const m = {};
      const list = r.data || [];
      list.forEach(c => { m[c.id] = c; });
      setCampaignsMap(m);
      setCampaigns(list);
    }).catch(() => {});
    api.get('/users/commerciaux').then(r => setCommerciaux(r.data || [])).catch(() => {});
    // Initial targets load
    loadTargets();
    loadForbiddenPositions();
    api.get('/maps/targets/stats').then(r => setTargetsStats(r.data)).catch(() => {});
    api.get('/maps/forbidden-positions/stats').then(r => setForbiddenStats(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTargets = async () => {
    const groups = [];
    if (targetFilters.showIntermarche) groups.push('Intermarché');
    if (targetFilters.showSystemeU) groups.push('Système U');
    if (targetFilters.showLeclerc) groups.push('Leclerc');
    if (groups.length === 0) { setTargets([]); return; }
    try {
      const params = new URLSearchParams();
      params.set('enseigne_group', groups.join(','));
      if (targetFilters.excludeHanagroup) params.set('exclude_hanagroup', 'true');
      if (targetFilters.excludeWasabi) params.set('exclude_wasabi', 'true');
      const { data } = await api.get(`/maps/targets?${params.toString()}`);
      setTargets(data.targets || []);
    } catch {}
  };

  const loadForbiddenPositions = async () => {
    try {
      const { data } = await api.get('/maps/forbidden-positions?only_localized=true');
      setForbiddenPositions(data.positions || []);
    } catch {}
  };

  useEffect(() => { if (showTargets) loadTargets(); }, [targetFilters, showTargets]); // eslint-disable-line

  // Filter prospects by radius if set
  const visibleProspects = useMemo(() => {
    if (!radiusCenter || !radiusKm) return prospects;
    const R = 6371;
    const toRad = x => (x * Math.PI) / 180;
    return prospects.filter(p => {
      const dLat = toRad(p.latitude - radiusCenter[0]);
      const dLon = toRad(p.longitude - radiusCenter[1]);
      const a = Math.sin(dLat/2)**2 + Math.cos(toRad(radiusCenter[0])) * Math.cos(toRad(p.latitude)) * Math.sin(dLon/2)**2;
      const d = 2 * R * Math.asin(Math.sqrt(a));
      return d <= radiusKm;
    });
  }, [prospects, radiusCenter, radiusKm]);

  const handleGeocodeAll = async () => {
    if (!window.confirm("Géocoder tous les prospects sans coordonnées ? L'opération tourne en arrière-plan (~1 sec/prospect).")) return;
    setGeocoding(true);
    try {
      await api.post('/maps/geocode-all');
      toast.success("Géocodage démarré. Actualisez dans quelques minutes.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur géocodage");
    } finally {
      setTimeout(() => { loadStats(); setGeocoding(false); }, 2000);
    }
  };

  const handleCitySearch = async () => {
    if (!citySearch.trim()) return;
    try {
      const { data } = await api.get(`/maps/geocode?q=${encodeURIComponent(citySearch)}`);
      setCenter([data.lat, data.lon]);
      setZoom(11);
      setRadiusCenter([data.lat, data.lon]);
      if (!radiusKm) setRadiusKm(20);
      toast.success(`Zoom sur ${data.display_name.split(',').slice(0,2).join(',')}`);
    } catch (e) {
      toast.error("Lieu introuvable");
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const c = [pos.coords.latitude, pos.coords.longitude];
        setCenter(c); setZoom(11); setRadiusCenter(c); if (!radiusKm) setRadiusKm(10);
        toast.success("Localisation activée");
      },
      () => toast.error("Impossible d'obtenir votre position"),
    );
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const selectAllVisible = () => {
    const s = new Set(selected);
    visibleProspects.forEach(p => s.add(p.id));
    setSelected(s);
  };

  const clearSelection = () => setSelected(new Set());

  // === Autour: load curated targets (non-HANAGROUP) + forbidden HANAGROUP positions around a point ===
  const loadSurround = async (lat, lon, r = surroundRadius) => {
    setSurroundLoading(true);
    setShowSurround(true);
    setSurroundCenter([lat, lon]);
    setSurroundRadius(r);
    try {
      const [targetsResp, forbResp] = await Promise.all([
        api.get(`/maps/targets-nearby?lat=${lat}&lon=${lon}&radius_km=${r}&exclude_hanagroup=true`).catch(() => ({ data: { targets: [] } })),
        api.get(`/maps/forbidden-nearby?lat=${lat}&lon=${lon}&radius_km=${r}`).catch(() => ({ data: { positions: [] } })),
      ]);
      setSurroundSupermarkets(targetsResp.data.targets || []);
      setSurroundForbidden(forbResp.data.positions || []);
      toast.success(`${(targetsResp.data.targets || []).length} cibles • ${(forbResp.data.positions || []).length} positions HANAGROUP`);
    } catch (e) {
      toast.error("Erreur chargement Autour");
    } finally {
      setSurroundLoading(false);
    }
  };

  const clearSurround = () => {
    setShowSurround(false);
    setSurroundCenter(null);
    setSurroundSupermarkets([]);
    setSurroundForbidden([]);
  };

  // Open full prospect fiche
  const openProspectFiche = async (id) => {
    try {
      const { data } = await api.get(`/prospects/${id}`);
      setDrawerProspect(data);
    } catch { toast.error("Impossible d'ouvrir la fiche"); }
  };

  const computeTournee = async () => {
    if (selected.size < 2) { toast.error("Sélectionnez au moins 2 prospects"); return; }
    if (selected.size > 25) { toast.error("Maximum 25 prospects par tournée"); return; }
    try {
      const payload = { prospect_ids: [...selected] };
      if (radiusCenter) { payload.start_lat = radiusCenter[0]; payload.start_lon = radiusCenter[1]; }
      const { data } = await api.post('/maps/tournee', payload);
      setTourneeResult(data);
      toast.success(`Tournée optimisée: ${data.total_distance_km} km, ~${Math.round(data.estimated_minutes/60)}h${data.estimated_minutes%60}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur tournée");
    }
  };

  return (
    <div className="h-[calc(100vh-3rem)] flex flex-col gap-3" data-testid="map-page">
      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Carte & Tournées</h1>
          <p className="text-xs text-[#71717A]">
            {stats.localized}/{stats.total} localisés
            {stats.pending > 0 && <span className="text-[#F59E0B]"> • {stats.pending} à géocoder</span>}
            {stats.failed > 0 && <span className="text-[#EF4444]"> • {stats.failed} échec</span>}
          </p>
        </div>
        <div className="flex-1 flex items-center gap-2 min-w-[280px]">
          <div className="flex-1 flex gap-2">
            <Input
              data-testid="city-search-input"
              placeholder="🔎 Rechercher une ville (Toulouse, Lyon, Bordeaux...)"
              value={citySearch}
              onChange={e => setCitySearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCitySearch()}
              className="h-9"
            />
            <Button size="sm" onClick={handleCitySearch} data-testid="city-search-btn"><Search className="w-4 h-4"/></Button>
          </div>
          <Button size="sm" variant="outline" onClick={handleUseMyLocation} data-testid="locate-me-btn">
            <Locate className="w-4 h-4 mr-1"/>Ma position
          </Button>
          <Select value={radiusKm ? String(radiusKm) : ''} onValueChange={v => { if (v === 'off') { setRadiusKm(null); setRadiusCenter(null); } else setRadiusKm(Number(v)); }}>
            <SelectTrigger className="w-[130px] h-9" data-testid="radius-select"><SelectValue placeholder="Rayon..."/></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Pas de rayon</SelectItem>
              <SelectItem value="5">5 km</SelectItem>
              <SelectItem value="10">10 km</SelectItem>
              <SelectItem value="20">20 km</SelectItem>
              <SelectItem value="50">50 km</SelectItem>
              <SelectItem value="100">100 km</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
          <Filter className="w-4 h-4 mr-1"/>Filtres
        </Button>
        <Button size="sm" variant="outline" onClick={handleGeocodeAll} disabled={geocoding} data-testid="geocode-all-btn">
          {geocoding ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <RefreshCw className="w-4 h-4 mr-1"/>}
          Géocoder
        </Button>
      </div>

      {/* Filters bar */}
      {showFilters && (
        <Card className="p-3 flex items-center gap-3 flex-wrap">
          <Select value={filters.special} onValueChange={v => setFilters(f => ({...f, special: v === 'all' ? '' : v}))}>
            <SelectTrigger className="w-[200px] h-9" data-testid="special-filter"><SelectValue placeholder="Type de prospect"/></SelectTrigger>
            <SelectContent>
              {SPECIAL_FILTERS.map(f => <SelectItem key={f.value || 'all'} value={f.value || 'all'}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.enseigne} onValueChange={v => setFilters(f => ({...f, enseigne: v === 'all' ? '' : v}))}>
            <SelectTrigger className="w-[180px] h-9" data-testid="enseigne-filter"><SelectValue placeholder="Enseigne"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes enseignes</SelectItem>
              {facets.enseignes.map(e => <SelectItem key={e.value} value={e.value}>{e.value} ({e.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.statut} onValueChange={v => setFilters(f => ({...f, statut: v === 'all' ? '' : v}))}>
            <SelectTrigger className="w-[180px] h-9" data-testid="statut-filter"><SelectValue placeholder="Statut"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(STATUT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.departement} onValueChange={v => setFilters(f => ({...f, departement: v === 'all' ? '' : v}))}>
            <SelectTrigger className="w-[140px] h-9" data-testid="dept-filter"><SelectValue placeholder="Département"/></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">Tous départements</SelectItem>
              {facets.departements.map(d => <SelectItem key={d.value} value={d.value}>{d.value} ({d.count})</SelectItem>)}
            </SelectContent>
          </Select>
          {(filters.enseigne || filters.statut || filters.departement || filters.special) && (
            <Button size="sm" variant="ghost" onClick={() => setFilters({ enseigne: '', statut: '', departement: '', special: '' })} data-testid="clear-filters-btn">
              <X className="w-3 h-3 mr-1"/>Réinitialiser
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-[#71717A]">{visibleProspects.length} sur la carte</span>
            {selected.size > 0 && <Badge className="bg-[#F97316]">{selected.size} sélectionnés</Badge>}
          </div>
        </Card>
      )}

      {/* Main map + right panel */}
      <div className="flex-1 flex gap-3 min-h-0">
        <div className="flex-1 relative rounded-lg overflow-hidden border border-[#E5E7EB]" data-testid="map-container">
          {loading && (
            <div className="absolute inset-0 bg-white/80 z-[500] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#F97316]"/>
            </div>
          )}
          <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Recenter center={center} zoom={zoom}/>
            {radiusCenter && radiusKm && (
              <>
                <Circle center={radiusCenter} radius={radiusKm * 1000} pathOptions={{ color: '#F97316', fillOpacity: 0.05, weight: 2, dashArray: '5 5' }}/>
                <CircleMarker center={radiusCenter} radius={6} pathOptions={{ color: '#000', fillColor: '#F97316', fillOpacity: 1 }}/>
              </>
            )}
            {visibleProspects.map(p => {
              const camp = campaignsMap[p.campagne_id];
              const concurrent = (camp && camp.concurrent) || p.prestataire_actuel || '';
              // Use campaign color if prospect is in a campaign, fallback to statut color
              const markerColor = camp?.couleur || STATUT_COLORS[p.statut_commercial] || '#9CA3AF';
              return (
              <Marker
                key={p.id}
                position={[p.latitude, p.longitude]}
                icon={makeIcon(markerColor, selected.has(p.id))}
                eventHandlers={{
                  click: () => {
                    // Auto-load surround (targets + HANAGROUP) when clicking a prospect
                    if (!showSurround || !surroundCenter || surroundCenter[0] !== p.latitude || surroundCenter[1] !== p.longitude) {
                      loadSurround(p.latitude, p.longitude, surroundRadius);
                    }
                  }
                }}
              >
                <Popup>
                  <div className="min-w-[240px]" data-testid={`map-popup-${p.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <strong className="text-sm leading-tight">{p.enseigne}</strong>
                      <Badge style={{ background: STATUT_COLORS[p.statut_commercial], color: 'white' }} className="text-[10px] flex-shrink-0">
                        {STATUT_LABELS[p.statut_commercial] || p.statut_commercial}
                      </Badge>
                    </div>
                    {p.nom_magasin && (
                      <div className="text-xs font-medium text-[#27272A] mb-0.5">{p.nom_magasin}</div>
                    )}
                    <div className="text-xs text-[#71717A] mb-1.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3"/>{p.ville}{p.code_postal ? ` (${p.code_postal})` : ''}
                    </div>
                    {concurrent && (
                      <div className="mb-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#FEF2F2] text-[#B91C1C] font-medium">
                          <Swords className="w-3 h-3"/> Concurrent : {concurrent}
                        </span>
                      </div>
                    )}
                    {camp && (
                      <div className="text-[10px] text-[#71717A] mb-1.5">Campagne : {camp.nom}</div>
                    )}
                    {p.telephone && p.telephone !== '?' && (
                      <div className="text-xs flex items-center gap-1 mb-1">
                        <Phone className="w-3 h-3"/>
                        <a href={`tel:${p.telephone}`} className="text-[#F97316] font-medium">{p.telephone}</a>
                      </div>
                    )}
                    {(p.prenom_dirigeant || p.nom_dirigeant) && (
                      <div className="text-xs text-[#8B5CF6] font-medium mb-1">👤 {p.prenom_dirigeant} {p.nom_dirigeant}</div>
                    )}
                    {p.dernier_appel && (
                      <div className="text-[10px] text-[#71717A] mb-2">Dernier appel: {new Date(p.dernier_appel).toLocaleDateString('fr-FR')}</div>
                    )}
                    <Button size="sm" variant="default"
                      onClick={() => openProspectFiche(p.id)}
                      className="w-full text-xs h-7 mb-1 bg-[#0055FF] hover:bg-[#0044CC]"
                      data-testid={`open-fiche-${p.id}`}>
                      <ExternalLink className="w-3 h-3 mr-1"/>Voir la fiche
                    </Button>
                    <div className="flex gap-2">
                      <Button size="sm" variant={selected.has(p.id) ? 'default' : 'outline'}
                        onClick={() => toggleSelect(p.id)}
                        className={`text-xs h-7 flex-1 ${selected.has(p.id) ? 'bg-[#F97316] hover:bg-[#EA580C]' : ''}`}
                        data-testid={`select-prospect-${p.id}`}
                      >
                        {selected.has(p.id) ? <><CheckCircle2 className="w-3 h-3 mr-1"/>Sélectionné</> : '+ Tournée'}
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => {
                          setCenter([p.latitude, p.longitude]); setZoom(12);
                          loadSurround(p.latitude, p.longitude, surroundRadius);
                        }}
                        className="text-xs h-7 flex-1 border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED]"
                        data-testid={`nearby-prospect-${p.id}`}
                      >
                        <Target className="w-3 h-3 mr-1"/>Autour
                      </Button>
                    </div>
                  </div>
                </Popup>
              </Marker>
              );
            })}

            {/* Autour: zone circulaire */}
            {showSurround && surroundCenter && (
              <Circle center={surroundCenter} radius={surroundRadius * 1000}
                pathOptions={{ color: '#0055FF', fillOpacity: 0.04, weight: 2, dashArray: '8 4' }}/>
            )}

            {/* Curated targets (Intermarché VERT / Système U VIOLET / Leclerc BLEU) */}
            {showTargets && targets.map(t => (
              <Marker key={`tg-${t.id}`} position={[t.latitude, t.longitude]}
                icon={makeTargetIcon(t.enseigne_group)}>
                <Popup>
                  <div className="min-w-[240px]" data-testid={`target-popup-${t.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <strong className="text-sm" style={{ color: TARGET_GROUP_COLORS[t.enseigne_group] }}>{t.enseigne_detail}</strong>
                      <Badge className="text-[10px]" style={{ background: TARGET_GROUP_COLORS[t.enseigne_group] }}>
                        {t.enseigne_group}
                      </Badge>
                    </div>
                    <div className="text-xs text-[#71717A] mb-1">{t.rue}</div>
                    <div className="text-xs text-[#27272A] mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3"/>{[t.code_postal, t.commune || t.ville].filter(Boolean).join(' ')}
                    </div>
                    {t.ca_estime > 0 && (
                      <div className="mb-2">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-[#FEF3C7] text-[#B45309] font-semibold">
                          💰 CA estimé : {formatCA(t.ca_estime)}
                        </span>
                      </div>
                    )}
                    {t.hanagroup_match && (
                      <div className="mb-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#FEF2F2] text-[#B91C1C] font-medium">
                          ⚠ Position HANAGROUP détectée
                        </span>
                      </div>
                    )}
                    {t.wasabi_client_match && (
                      <div className="mb-1.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#ECFDF5] text-[#065F46] font-semibold border border-[#10B981]/30">
                          ✓ Déjà CLIENT WASABI — ne pas démarcher
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-1 mt-2">
                      <Button size="sm" className="h-7 text-xs bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50"
                        disabled={t.wasabi_client_match}
                        onClick={() => setTargetAddModal({ target: t, mode: 'existing' })}
                        data-testid={`target-add-existing-${t.id}`}>
                        + Ajouter à une campagne existante
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-[#F97316] text-[#F97316] disabled:opacity-50"
                        disabled={t.wasabi_client_match}
                        onClick={() => setTargetAddModal({ target: t, mode: 'new' })}
                        data-testid={`target-add-new-${t.id}`}>
                        + Créer une nouvelle campagne
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs disabled:opacity-50"
                        disabled={t.wasabi_client_match}
                        onClick={() => setTargetAddModal({ target: t, mode: 'free' })}
                        data-testid={`target-add-free-${t.id}`}>
                        + Ajouter aux prospects (hors campagne)
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                        onClick={() => { setCenter([t.latitude, t.longitude]); setZoom(12); loadSurround(t.latitude, t.longitude, surroundRadius); }}>
                        <Target className="w-3 h-3 mr-1"/>Autour
                      </Button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Persistent HANAGROUP positions (always visible) */}
            {showForbidden && forbiddenPositions.map((f, i) => (
              <Marker key={`fb-all-${i}-${f.ville}`} position={[f.latitude, f.longitude]}
                icon={makeSquareIcon('#F97316', '⛔')}>
                <Popup>
                  <div className="min-w-[200px]" data-testid={`forbidden-all-popup-${i}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <strong className="text-sm">{f.enseigne}</strong>
                      <Badge className="text-[10px] bg-[#F97316]">HANAGROUP</Badge>
                    </div>
                    <div className="text-xs text-[#27272A] mb-1">{f.ville}</div>
                    {f.concept && <div className="text-[11px] text-[#71717A]">Concept : {f.concept}</div>}
                    <div className="text-[10px] text-[#B91C1C] mt-1 font-medium">⚠ Ne pas démarcher (accord HANAGROUP)</div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Autour: positions interdites HANAGROUP (orange) */}
            {showSurround && surroundForbidden.map((f, i) => (
              <Marker key={`fb-${i}-${f.ville}`} position={[f.latitude, f.longitude]}
                icon={makeSquareIcon('#F97316', '⛔')}>
                <Popup>
                  <div className="min-w-[200px]" data-testid={`forbidden-popup-${i}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <strong className="text-sm">{f.enseigne}</strong>
                      <Badge className="text-[10px] bg-[#F97316]">position HANAGROUP</Badge>
                    </div>
                    <div className="text-xs text-[#27272A] mb-1">{f.ville}</div>
                    {f.concept && <div className="text-[11px] text-[#71717A]">Concept : {f.concept}</div>}
                    <div className="text-[10px] text-[#B91C1C] mt-1 font-medium">⚠ Ne pas démarcher (accord HANAGROUP)</div>
                    <div className="text-[10px] text-[#71717A] mt-0.5">📏 {f.distance_km} km</div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Campaigns legend panel (top-left under search) */}
          {campaigns.length > 0 && (
            <div className="absolute top-3 left-3 z-[400] bg-white rounded-lg shadow-lg border border-[#E5E7EB] p-3 w-[230px] max-h-[320px] overflow-hidden flex flex-col" data-testid="campaigns-legend-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Route className="w-4 h-4 text-[#F97316]"/>
                  <span className="text-sm font-semibold">Mes campagnes</span>
                </div>
                <span className="text-[10px] text-[#71717A]">{campaigns.length}</span>
              </div>
              <div className="overflow-y-auto space-y-1 flex-1">
                {campaigns.map(c => {
                  const count = prospects.filter(p => p.campagne_id === c.id && p.latitude).length;
                  return (
                    <button key={c.id}
                      onClick={() => {
                        // Zoom to campaign bounds
                        const pts = prospects.filter(p => p.campagne_id === c.id && p.latitude);
                        if (pts.length > 0) {
                          const avgLat = pts.reduce((s, p) => s + p.latitude, 0) / pts.length;
                          const avgLon = pts.reduce((s, p) => s + p.longitude, 0) / pts.length;
                          setCenter([avgLat, avgLon]);
                          setZoom(pts.length === 1 ? 11 : 8);
                        }
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F4F4F5] text-left"
                      data-testid={`campaign-legend-${c.id}`}>
                      <span className="w-3 h-3 rounded-full flex-shrink-0 border border-white shadow-sm" style={{ background: c.couleur || '#9CA3AF' }}/>
                      <span className="flex-1 truncate text-xs font-medium">{c.nom}</span>
                      <span className="text-[10px] text-[#71717A] tabular-nums">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Targets legend & filter panel (top-right) */}
          <div className="absolute top-3 right-3 z-[400] bg-white rounded-lg shadow-lg border border-[#E5E7EB] p-3 w-[230px]" data-testid="targets-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-[#F97316]"/>
                <span className="text-sm font-semibold">Cibles GMS (≥30M€)</span>
              </div>
              <button onClick={() => setShowTargets(s => !s)} className="text-[10px] text-[#71717A] hover:text-[#F97316]" data-testid="toggle-targets-btn">
                {showTargets ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            {showTargets && (
              <div className="space-y-1.5 text-xs">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-itm">
                  <input type="checkbox" checked={targetFilters.showIntermarche}
                    onChange={e => setTargetFilters(f => ({...f, showIntermarche: e.target.checked}))}/>
                  <span className="w-3 h-3 rounded-full bg-[#10B981]"/>
                  <span className="flex-1">Intermarché</span>
                  <span className="text-[10px] text-[#71717A]">{targetsStats.by_group?.find(g => g.enseigne_group === 'Intermarché')?.count || 85}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-systeme-u">
                  <input type="checkbox" checked={targetFilters.showSystemeU}
                    onChange={e => setTargetFilters(f => ({...f, showSystemeU: e.target.checked}))}/>
                  <span className="w-3 h-3 rounded-full bg-[#8B5CF6]"/>
                  <span className="flex-1">Système U</span>
                  <span className="text-[10px] text-[#71717A]">{targetsStats.by_group?.find(g => g.enseigne_group === 'Système U')?.count || 136}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-leclerc">
                  <input type="checkbox" checked={targetFilters.showLeclerc}
                    onChange={e => setTargetFilters(f => ({...f, showLeclerc: e.target.checked}))}/>
                  <span className="w-3 h-3 rounded-full bg-[#0055FF]"/>
                  <span className="flex-1">Leclerc</span>
                  <span className="text-[10px] text-[#71717A]">{targetsStats.by_group?.find(g => g.enseigne_group === 'Leclerc')?.count || 244}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer pt-1.5 border-t border-[#E5E7EB]" data-testid="toggle-show-hanagroup">
                  <input type="checkbox" checked={showForbidden}
                    onChange={e => setShowForbidden(e.target.checked)}/>
                  <span className="w-3 h-3 rounded-sm bg-[#F97316] border border-white shadow-sm transform rotate-45"/>
                  <span className="flex-1 text-[11px]">Afficher positions <strong className="text-[#B91C1C]">HANAGROUP</strong></span>
                  <span className="text-[10px] text-[#71717A]">{forbiddenPositions.length}/{forbiddenStats.total || '?'}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-exclude-hana">
                  <input type="checkbox" checked={targetFilters.excludeHanagroup}
                    onChange={e => setTargetFilters(f => ({...f, excludeHanagroup: e.target.checked}))}/>
                  <span className="flex-1 text-[11px] pl-5">↳ Masquer cibles dans villes HANAGROUP</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-exclude-wasabi">
                  <input type="checkbox" checked={targetFilters.excludeWasabi}
                    onChange={e => setTargetFilters(f => ({...f, excludeWasabi: e.target.checked}))}/>
                  <span className="flex-1 text-[11px]">Exclure clients <strong className="text-[#065F46]">WASABI</strong></span>
                </label>
                <div className="text-[10px] text-[#71717A] pt-1.5 border-t border-[#E5E7EB]">
                  <strong>{targets.length}</strong> cible{targets.length > 1 ? 's' : ''} affichée{targets.length > 1 ? 's' : ''}
                  {targetsStats.total > 0 && <> • {targetsStats.localized}/{targetsStats.total} géocodées</>}
                </div>
              </div>
            )}
          </div>

          {/* Autour overlay panel (bottom-left) */}
          {showSurround && surroundCenter && (
            <div className="absolute bottom-3 left-3 z-[400] bg-white rounded-lg shadow-lg border border-[#E5E7EB] p-3 min-w-[240px] max-w-[300px]" data-testid="surround-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-[#0055FF]"/>
                  <span className="text-sm font-semibold">Autour du point</span>
                </div>
                <button onClick={clearSurround} className="text-[#71717A] hover:text-[#EF4444]" data-testid="close-surround-btn">
                  <X className="w-4 h-4"/>
                </button>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-[#71717A]">Rayon :</span>
                <select
                  value={surroundRadius}
                  onChange={e => loadSurround(surroundCenter[0], surroundCenter[1], Number(e.target.value))}
                  className="h-7 text-xs flex-1 bg-white border border-[#E5E7EB] rounded px-2 focus:outline-none focus:ring-2 focus:ring-[#F97316]/30"
                  data-testid="surround-radius-select"
                >
                  {[5, 10, 15, 20, 30, 50, 75, 100].map(v => (
                    <option key={v} value={v}>{v} km</option>
                  ))}
                </select>
              </div>
              {surroundLoading ? (
                <div className="flex items-center gap-2 text-xs text-[#71717A]">
                  <Loader2 className="w-3 h-3 animate-spin"/> Recherche...
                </div>
              ) : (
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[#10B981]"/>
                    <span className="w-3 h-3 rounded-full bg-[#8B5CF6]"/>
                    <span className="w-3 h-3 rounded-full bg-[#0055FF]"/>
                    <span className="flex-1">Cibles GMS (≥30M€ CA) <strong>{surroundSupermarkets.length}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-[#F97316] border border-white shadow-sm"/>
                    <span className="flex-1">Positions <strong className="text-[#B91C1C]">HANAGROUP</strong> <strong>{surroundForbidden.length}</strong></span>
                  </div>
                  <div className="text-[10px] text-[#71717A] pt-1 leading-tight">
                    🎯 Cibles Intermarché (vert), Système U (violet), Leclerc (bleu) — hors HANAGROUP<br/>
                    ⛔ Orange = positions HANAGROUP
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right panel */}
        <Card className="w-80 flex-shrink-0 flex flex-col p-3 gap-2 overflow-hidden" data-testid="tournee-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm flex items-center gap-1"><Route className="w-4 h-4"/>Ma tournée</h3>
            {selected.size > 0 && <Badge className="bg-[#F97316]">{selected.size}</Badge>}
          </div>
          {selected.size === 0 && !tourneeResult && (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-4 text-xs text-[#71717A]">
              <Navigation className="w-10 h-10 mb-3 text-[#D4D4D8]"/>
              <p className="mb-2">Sélectionnez plusieurs prospects sur la carte</p>
              <p className="text-[10px]">Puis cliquez sur <strong>Créer ma tournée</strong> pour obtenir l'itinéraire optimisé.</p>
            </div>
          )}
          {selected.size > 0 && !tourneeResult && (
            <>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-[#F97316] hover:bg-[#EA580C]" onClick={computeTournee} data-testid="compute-tournee-btn">
                  <Route className="w-4 h-4 mr-1"/>Créer ma tournée
                </Button>
                <Button size="sm" variant="outline" onClick={clearSelection} data-testid="clear-selection-btn"><X className="w-3 h-3"/></Button>
              </div>
              <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="select-all-visible-btn">
                <Target className="w-3 h-3 mr-1"/>Tout sélectionner ({visibleProspects.length})
              </Button>
              <div className="flex-1 overflow-y-auto space-y-1 mt-2">
                {[...selected].map(id => {
                  const p = prospects.find(x => x.id === id);
                  if (!p) return null;
                  return (
                    <div key={id} className="text-xs p-2 bg-[#F4F4F5] rounded flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{p.nom_magasin || p.enseigne}</div>
                        <div className="text-[#71717A] truncate">{p.ville}</div>
                      </div>
                      <button onClick={() => toggleSelect(id)} className="text-[#71717A] hover:text-[#EF4444]">
                        <X className="w-3 h-3"/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {tourneeResult && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white rounded-lg p-3 mb-2">
                <div className="text-xs opacity-80">Tournée optimisée</div>
                <div className="text-lg font-bold">{tourneeResult.total_distance_km} km</div>
                <div className="text-xs">
                  ⏱ ~{Math.floor(tourneeResult.estimated_minutes/60)}h{String(tourneeResult.estimated_minutes%60).padStart(2,'0')}
                  • {tourneeResult.count} arrêts
                </div>
              </div>
              <Button size="sm" className="mb-2 bg-[#10B981] hover:bg-[#059669]"
                onClick={() => window.open(tourneeResult.google_maps_url, '_blank')}
                data-testid="open-gmaps-btn"
              >
                <Navigation className="w-4 h-4 mr-1"/>Ouvrir dans Google Maps
              </Button>
              <div className="flex-1 overflow-y-auto space-y-1">
                {tourneeResult.ordered.map((p, i) => (
                  <div key={p.id} className="text-xs p-2 border border-[#E5E7EB] rounded">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#F97316] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {i+1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{p.nom_magasin || p.enseigne}</div>
                        <div className="text-[#71717A] truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3"/>{p.ville}
                        </div>
                        {p.distance_from_previous_km > 0 && (
                          <div className="text-[10px] text-[#F97316]">→ {p.distance_from_previous_km} km</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => { setTourneeResult(null); clearSelection(); }} className="mt-2" data-testid="reset-tournee-btn">
                Nouvelle tournée
              </Button>
            </div>
          )}
        </Card>
      </div>
      {drawerProspect && (
        <ProspectDrawer
          prospect={drawerProspect}
          onClose={() => setDrawerProspect(null)}
          onUpdate={() => loadProspects()}
          commerciaux={commerciaux}
        />
      )}
      {targetAddModal && (
        <TargetAddModal
          target={targetAddModal.target}
          mode={targetAddModal.mode}
          campaigns={campaigns}
          onClose={() => setTargetAddModal(null)}
          onSuccess={(res) => {
            setTargetAddModal(null);
            loadProspects();
            if (res.campagne_id) {
              api.get('/campaigns').then(r => {
                const m = {}; (r.data || []).forEach(c => { m[c.id] = c; });
                setCampaignsMap(m); setCampaigns(r.data || []);
              }).catch(() => {});
            }
          }}
        />
      )}
    </div>
  );
}

// ==== TargetAddModal ====
function TargetAddModal({ target, mode, campaigns, onClose, onSuccess }) {
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [newCampName, setNewCampName] = useState(`Cible ${target.enseigne_group} - ${target.commune || target.ville}`);
  const [newCampConcurrent, setNewCampConcurrent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {};
      if (mode === 'existing') {
        if (!selectedCampaign) { toast.error('Sélectionnez une campagne'); setSubmitting(false); return; }
        payload.campagne_id = selectedCampaign;
      } else if (mode === 'new') {
        if (!newCampName.trim()) { toast.error('Nom de campagne requis'); setSubmitting(false); return; }
        payload.nouvelle_campagne_nom = newCampName.trim();
        payload.nouvelle_campagne_concurrent = newCampConcurrent.trim();
      }
      const { data } = await api.post(`/maps/targets/${target.id}/to-prospect`, payload);
      if (data.already_exists) {
        toast.info('Ce prospect existe déjà dans la base');
      } else {
        const enrichMsg = data.enrichment_queued ? ' • 🔍 Enrichissement auto en cours' : '';
        toast.success(`Prospect créé${data.campagne_id ? ' et ajouté à la campagne' : ' (hors campagne)'}${enrichMsg}`);
      }
      onSuccess(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === 'wasabi_client') {
        toast.error(`🛡 ${detail.message}`, { duration: 6000 });
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Erreur');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="target-add-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
              {mode === 'existing' && 'Ajouter à une campagne existante'}
              {mode === 'new' && 'Créer une nouvelle campagne'}
              {mode === 'free' && 'Ajouter aux prospects (hors campagne)'}
            </h3>
            <p className="text-xs text-[#71717A] mt-0.5">{target.enseigne_detail} • {target.commune || target.ville}</p>
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#EF4444]"><X className="w-4 h-4"/></button>
        </div>
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 mb-4 text-xs space-y-1">
          <div><strong>Enseigne :</strong> {target.enseigne_detail}</div>
          <div><strong>Adresse :</strong> {target.rue}</div>
          <div><strong>Code postal :</strong> {target.code_postal} • <strong>Ville :</strong> {target.commune || target.ville}</div>
          {target.ca_estime > 0 && <div><strong>CA estimé :</strong> {(target.ca_estime/1_000_000).toFixed(1)} M€</div>}
        </div>
        {mode === 'existing' && (
          <div className="mb-4">
            <label className="text-xs font-medium mb-1.5 block">Campagne</label>
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="h-10" data-testid="target-campaign-select">
                <SelectValue placeholder="Sélectionner..."/>
              </SelectTrigger>
              <SelectContent>
                {campaigns.length === 0 && <div className="p-2 text-xs text-[#71717A]">Aucune campagne</div>}
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nom} {c.concurrent ? `(vs ${c.concurrent})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {mode === 'new' && (
          <>
            <div className="mb-3">
              <label className="text-xs font-medium mb-1.5 block">Nom de la campagne</label>
              <Input value={newCampName} onChange={e => setNewCampName(e.target.value)}
                className="h-10" data-testid="target-newcamp-name"/>
            </div>
            <div className="mb-4">
              <label className="text-xs font-medium mb-1.5 block">Prestataire en place ciblé (optionnel)</label>
              <Input value={newCampConcurrent} onChange={e => setNewCampConcurrent(e.target.value)}
                placeholder="Sushiman, Hanagroup, Aucun..."
                className="h-10" data-testid="target-newcamp-concurrent"/>
            </div>
          </>
        )}
        {mode === 'free' && (
          <div className="mb-4 text-xs text-[#71717A] p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg">
            Le prospect sera créé sans être associé à une campagne. Vous pourrez l'assigner ultérieurement.
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>Annuler</Button>
          <Button onClick={submit} className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white" disabled={submitting} data-testid="target-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Confirmer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
